import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Card,
  CardBody,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import {
  getNetworkReportFactors,
  getNetworkReportOrganizations,
  getNetworkReportSummary,
} from "../networkReportApi";
import LowestPerformingFactorsPreview from "../components/LowestPerformingFactorsPreview";
import NetworkOrganizationQuilt from "../components/NetworkOrganizationQuilt";
import NetworkOrganizationStatusSummary from "../components/NetworkOrganizationStatusSummary";

const NetworkReportOverviewTab = ({ heading = "Overview" }) => {
  const { networkId } = useParams();
  const toast = useToast();
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const [summary, setSummary] = useState(null);
  const [factors, setFactors] = useState([]);
  const [factorLoading, setFactorLoading] = useState(true);
  const [factorError, setFactorError] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [organizationsError, setOrganizationsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadSummary = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getNetworkReportSummary(networkId);
        if (active) setSummary(result);
      } catch (err) {
        console.error("Error loading network report summary:", err);
        if (active) {
          setError("Could not load network report overview.");
          toast({ title: "Could not load network report overview", status: "error", duration: 4000, isClosable: true });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    const loadFactors = async () => {
      setFactorLoading(true);
      setFactorError(false);
      try {
        const result = await getNetworkReportFactors(networkId);
        if (active) setFactors(result);
      } catch (err) {
        console.error("Error loading network factor preview:", err);
        if (active) {
          setFactors([]);
          setFactorError(true);
        }
      } finally {
        if (active) setFactorLoading(false);
      }
    };

    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      setOrganizationsError(false);
      try {
        const result = await getNetworkReportOrganizations(networkId);
        if (active) setOrganizations(result);
      } catch (err) {
        console.error("Error loading network organization quilt:", err);
        if (active) {
          setOrganizations([]);
          setOrganizationsError(true);
        }
      } finally {
        if (active) setOrganizationsLoading(false);
      }
    };

    loadSummary();
    loadFactors();
    loadOrganizations();

    return () => {
      active = false;
    };
  }, [networkId, toast]);

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" label="Loading network report overview" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  if (!summary?.hasData) {
    return (
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <Heading size="sm" mb={2}>No overview data</Heading>
          <Text color={mutedColor}>No network report summary is available yet.</Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="md">{heading}</Heading>
        <Text mt={1} color={mutedColor}>
          {summary.networkName} report overview.
        </Text>
      </Box>

      <NetworkOrganizationStatusSummary
        statusCounts={summary.organizationStatusCounts}
        averageCompletionRate={summary.averageOrganizationCompletionRate}
      />

      <NetworkOrganizationQuilt
        organizations={organizations}
        loading={organizationsLoading}
        error={organizationsError}
        networkId={networkId}
      />

      <LowestPerformingFactorsPreview
        factors={factors}
        loading={factorLoading}
        error={factorError}
        factorsPath={`/app/network/${networkId}/report/factors`}
      />
    </VStack>
  );
};

export default NetworkReportOverviewTab;
